import { delay } from "./client";
import { getCart } from "./cart";
import { api, USE_MOCK_API } from "./http";
import type { Order, OrderPayload } from "./types";

export interface PlaceOrderInput extends OrderPayload {
  shippingAreaId?: string;
  /** Omit to check out the server-side cart; pass to check out an explicit list. */
  items?: { productId: string; color: string; size: string; quantity: number }[];
}

/** POST /api/orders — totals and stock are always resolved server-side. */
export async function createOrder(payload: PlaceOrderInput): Promise<Order> {
  if (!payload.address?.firstName || !payload.address?.address) {
    throw new Error("Please complete your shipping address.");
  }

  if (!USE_MOCK_API) {
    return api.post<Order>("/orders", {
      address: payload.address,
      paymentMethod: payload.paymentMethod,
      ...(payload.shippingAreaId ? { shippingAreaId: payload.shippingAreaId } : {}),
      ...(payload.items ? { items: payload.items } : {}),
    });
  }

  const cart = await getCart();
  return delay(
    {
      id: `SEEN-${Math.floor(Math.random() * 900000 + 100000)}`,
      createdAt: new Date().toISOString(),
      total: cart.total,
      status: "confirmed",
    },
    600,
  );
}

/** GET /api/orders */
export async function getMyOrders(): Promise<Order[]> {
  if (!USE_MOCK_API) return api.get<Order[]>("/orders");
  return delay([], 200);
}

/** GET /api/orders/:id */
export async function getMyOrder(id: string): Promise<Order | null> {
  if (!USE_MOCK_API) {
    try {
      return await api.get<Order>(`/orders/${encodeURIComponent(id)}`);
    } catch {
      return null;
    }
  }
  return delay(null, 160);
}

/** POST /api/payments/fawaterak/create */
export async function initiatePayment(orderId: string): Promise<{ success: boolean; paymentUrl: string; orderId: string; message?: string }> {
  const res = await api.post<any>("/payments/fawaterak/create", { orderId });
  return res?.data || res;
}

/** GET /api/payments/status/:orderId */
export async function getPaymentStatus(orderId: string, status?: string): Promise<{ orderId: string; orderNumber: string; status: string; paymentStatus: string; paymentMethod: string }> {
  const q = status ? `?status=${encodeURIComponent(status)}` : '';
  const res = await api.get<any>(`/payments/status/${encodeURIComponent(orderId)}${q}`);
  return res?.data || res;
}

